import { useState } from "react";
import { Link } from "react-router-dom";

export const Container = ({ className = "", children }) => (
    <div className={`mx-auto w-full max-w-6xl px-5 sm:px-8 ${className}`}>
        {children}
    </div>
);

export const Section = ({ className = "", children, id }) => (
    <section id={id} className={`py-20 sm:py-28 ${className}`}>
        {children}
    </section>
);

export const Eyebrow = ({ children, className = "" }) => (
    <span className={`eyebrow ${className}`}>{children}</span>
);

// The Aegis Line's gold rule — the brand's visual spine — as a divider.
export const RuleDivider = ({ className = "" }) => (
    <div className={`rule-line w-full ${className}`} aria-hidden="true" />
);

export const SectionHeading = ({ eyebrow, title, sub, className = "" }) => (
    <div className={`max-w-3xl ${className}`}>
        {eyebrow && <Eyebrow className="mb-4 block">{eyebrow}</Eyebrow>}
        <h2 className="text-3xl leading-tight text-text-hi sm:text-4xl lg:text-5xl">
            {title}
        </h2>
        {sub && (
            <p className="mt-5 text-base leading-relaxed text-text-lo sm:text-lg">
                {sub}
            </p>
        )}
    </div>
);

export const AmberButton = ({
    to,
    href,
    children,
    className = "",
    testId,
    ...rest
}) => {
    const cls = `btn-amber inline-flex items-center justify-center gap-2 rounded px-6 py-3 text-sm font-semibold ${className}`;
    if (to)
        return (
            <Link to={to} className={cls} data-testid={testId} {...rest}>
                {children}
            </Link>
        );
    return (
        <a href={href} className={cls} data-testid={testId} {...rest}>
            {children}
        </a>
    );
};

export const GhostButton = ({ to, children, className = "", testId }) => (
    <Link
        to={to}
        data-testid={testId}
        className={`inline-flex items-center justify-center gap-2 rounded border border-haze px-6 py-3 text-sm font-semibold text-text-hi transition-colors hover:border-rule hover:text-rule ${className}`}
    >
        {children}
    </Link>
);

// Inner-page hero header
export const PageHero = ({ eyebrow, title, sub, children }) => (
    <section className="crosshair-bg border-b border-haze">
        <Container className="py-16 sm:py-24">
            {eyebrow && <Eyebrow className="mb-5 block">{eyebrow}</Eyebrow>}
            <h1 className="max-w-4xl text-4xl font-extrabold leading-[1.08] text-text-hi sm:text-5xl">
                {title}
            </h1>
            {sub && (
                <p className="mt-6 max-w-2xl text-base leading-relaxed text-text-lo sm:text-lg">
                    {sub}
                </p>
            )}
            {children && <div className="mt-8">{children}</div>}
        </Container>
    </section>
);

// Card image with eager load + blur-up reveal. The sizing/border classes are
// passed via `className` (applied to the wrapper); the image fills it.
export const CardImage = ({ src, alt, className = "" }) => {
    const [loaded, setLoaded] = useState(false);
    return (
        <div className={`relative overflow-hidden bg-ink-soft ${className}`}>
            <div
                className={`pointer-events-none absolute inset-0 bg-gradient-to-br from-haze/60 via-ink-soft to-ink transition-opacity duration-700 ${
                    loaded ? "opacity-0" : "opacity-100 animate-pulse"
                }`}
            />
            <img
                src={src}
                alt={alt}
                loading="eager"
                decoding="async"
                onLoad={() => setLoaded(true)}
                className={`h-full w-full object-cover transition-[opacity,transform,filter] duration-700 ease-out group-hover:scale-105 ${
                    loaded ? "opacity-100 blur-0" : "opacity-0 blur-lg"
                }`}
            />
        </div>
    );
};

// Persistent mono "data chip"
export const Chip = ({ children, tone = "rule" }) => {
    const colors = {
        rule: "text-rule border-rule/40",
        signal: "text-signal border-signal/40",
        warn: "text-warn border-warn/40",
        lo: "text-text-lo border-haze",
    };
    return (
        <span
            className={`inline-flex items-center rounded-sm border bg-ink/40 px-2.5 py-1 font-mono text-[11px] ${colors[tone]}`}
        >
            {children}
        </span>
    );
};
